import type { Goal } from './types';
import type { GoalView, GoalInput } from '../../../api/PreformanceManagement/Goals';

// ----------------------
// BACKEND ↔ FRONTEND MAPPING FUNCTIONS
// ----------------------

// Helper functions to map backend enums to frontend strings
export const mapBackendLevelToFrontend = (level: number | string): 'company' | 'team' | 'individual' => {
  if (typeof level === 'string') {
    const lowerLevel = level.toLowerCase();
    if (lowerLevel === 'company') return 'company';
    if (lowerLevel === 'team') return 'team';
    if (lowerLevel === 'individual') return 'individual';
  }

  switch (level) {
    case 1: return 'company';     // GoalLevel.Company
    case 2: return 'team';        // GoalLevel.Team
    case 3: return 'individual';  // GoalLevel.Individual
    default: return 'individual';
  }
};

export const mapBackendStatusToFrontend = (status: number | string): 'on-track' | 'at-risk' | 'completed' | 'overdue' => {
  // Handle string enum names from backend
  if (typeof status === 'string') {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'ontrack':
      case 'on-track':
      case 'notstarted':
        return 'on-track';
      case 'atrisk':
      case 'at-risk':
        return 'at-risk';
      case 'completed':
        return 'completed';
      case 'overdue':
        return 'overdue';
      default:
        return 'on-track';
    }
  }

  // Handle numeric enum values
  switch (Number(status)) {
    case 1: return 'on-track';   // GoalStatus.OnTrack
    case 2: return 'at-risk';    // GoalStatus.AtRisk
    case 3: return 'completed';  // GoalStatus.Completed
    case 4: return 'overdue';    // GoalStatus.Overdue
    case 5: return 'on-track';   // GoalStatus.NotStarted (map to on-track)
    default: return 'on-track';
  }
};

export const mapBackendPriorityToFrontend = (priority: number | string): 'low' | 'medium' | 'high' => {
  // Handle string enum names from backend
  if (typeof priority === 'string') {
    const priorityLower = priority.toLowerCase();
    switch (priorityLower) {
      case 'low': return 'low';
      case 'medium': return 'medium';
      case 'high': return 'high';
      default: return 'medium';
    }
  }

  // Handle numeric values
  switch (priority) {
    case 0: return 'low';
    case 1: return 'medium';
    case 2: return 'high';
    default: return 'medium';
  }
};

// Helper functions to map frontend strings to backend enums
export const mapFrontendLevelToBackend = (level: string): number => {
  switch (level.toLowerCase()) {
    case 'company': return 1;     // GoalLevel.Company
    case 'team': return 2;        // GoalLevel.Team
    case 'individual': return 3;  // GoalLevel.Individual
    default: return 3;
  }
};

export const mapFrontendStatusToBackend = (status: string): number => {
  switch (status.toLowerCase()) {
    case 'on-track':
    case 'ontrack':
    case 'notstarted':
      return 1;   // GoalStatus.OnTrack
    case 'at-risk':
    case 'atrisk':
      return 2;   // GoalStatus.AtRisk
    case 'completed':
      return 3;   // GoalStatus.Completed
    case 'overdue':
      return 4;   // GoalStatus.Overdue
    default: return 1;
  }
};

export const mapFrontendPriorityToBackend = (priority: string): number => {
  switch (priority.toLowerCase()) {
    case 'low': return 0;
    case 'medium': return 1;
    case 'high': return 2;
    default: return 1;
  }
};

// ----------------------
// CONVERSION FUNCTIONS
// ----------------------

// Format dates without time zone conversion - just take the date part
const formatDateOnly = (dateString: string): string => {
  if (!dateString) return '';
  // Extract just the YYYY-MM-DD part, ignoring time and timezone
  return dateString.split('T')[0];
};

// Convert backend GoalView to frontend Goal interface
export const convertGoalViewToGoal = (goalView: GoalView): Goal => {
  // Handle both numeric and string level values
  const levelValue = typeof goalView.level === 'string'
    ? goalView.level.toLowerCase()
    : goalView.level;

  return {
    id: goalView.id || `goal-${Date.now()}`,
    title: goalView.title || '',
    description: goalView.description || '',
    level: mapBackendLevelToFrontend(levelValue),
    status: mapBackendStatusToFrontend(goalView.status),
    progress: goalView.progress || 0,
    owner: goalView.owner?.name || goalView.owner?.email || 'Unknown',
    ownerId: goalView.owner?.id || goalView.userId,
    department: goalView.department?.name || 'Unknown',
    departmentId: goalView.department?.id?.toString() || goalView.departmentId?.toString(),
    startDate: formatDateOnly(goalView.startDate),
    dueDate: formatDateOnly(goalView.dueDate),
    milestones: goalView.milestones?.map((milestone: Record<string, unknown>) => ({
      id: (milestone.id as number) || Date.now(),
      title: (milestone.title as string) || '',
      completed: (milestone.isCompleted as boolean) || false,
      dueDate: formatDateOnly(milestone.dueDate as string),
    })) || [],
    parentId: goalView.parentId,
    comments: goalView.comments?.map((comment: Record<string, unknown>) => ({
      id: (comment.id as number) || Date.now(),
      text: (comment.content as string) || (comment.text as string) || '',
      author: ((comment.user as Record<string, unknown>)?.name as string) || ((comment.author as Record<string, unknown>)?.name as string) || 'Unknown',
      authorId: ((comment.user as Record<string, unknown>)?.id as string) || ((comment.author as Record<string, unknown>)?.id as string) || '',
      createdAt: (comment.createdAt as string) || new Date().toISOString(),
      updatedAt: (comment.updatedAt as string) || new Date().toISOString(),
    })) || [],
    tags: goalView.tags || [],
    priority: mapBackendPriorityToFrontend(goalView.priority),
    metricType: goalView.metricType ?? null,
    skillIds: goalView.skillIds ?? null,
    isArchived: goalView.isDeleted || false,
    createdAt: goalView.createdAt,
    updatedAt: goalView.updatedAt,
    hasActiveBlockers: goalView.hasActiveBlockers || false,
    activeBlockerCount: goalView.activeBlockerCount || 0,
  };
};

// Convert frontend Goal to backend GoalInput
export const convertGoalToGoalInput = (goal: Partial<Goal>, currentUserId?: string): GoalInput => {
  return {
    title: goal.title || '',
    description: goal.description || '',
    level: goal.level ? mapFrontendLevelToBackend(goal.level) : mapFrontendLevelToBackend('individual'),
    status: goal.status ? mapFrontendStatusToBackend(goal.status) : mapFrontendStatusToBackend('on-track'),
    progress: goal.progress !== undefined ? goal.progress : 0,
    ownerId: goal.ownerId || currentUserId || '',
    // Only include departmentId if it has a valid value, otherwise null
    departmentId: goal.departmentId && goal.departmentId !== '' ? parseInt(String(goal.departmentId)) : null,
    // Only include categoryId if it has a valid value, otherwise null
    categoryId: goal.categoryId || null,
    // Only include parentId if it has a valid value, otherwise null
    parentId: goal.parentId || null,
    startDate: goal.startDate || new Date().toISOString().split('T')[0],
    dueDate: goal.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    tags: goal.tags || [],
    priority: goal.priority ? mapFrontendPriorityToBackend(goal.priority) : mapFrontendPriorityToBackend('medium'),
    metricType: goal.metricType ?? null,
    skillIds: goal.skillIds ?? null,
    milestones: goal.milestones?.map(m => ({
      title: m.title,
      description: '',
      isCompleted: m.completed,
      dueDate: m.dueDate,
      order: 0
    })) || []
  };
};
